import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/Common/SEO';
import { useTheme } from '../contexts/ThemeContext';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon, 
  ChatBubbleBottomCenterTextIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const Contact: React.FC = () => {
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tourInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const contactMethods = [
    {
      icon: PhoneIcon,
      label: "Call Our Experts",
      value: "+254 708 560 665",
      sub: "Available 24/7 for guest support",
      color: "blue"
    },
    {
      icon: EnvelopeIcon,
      label: "Electronic Mail",
      value: "sighttrailsafaris@gmail.com",
      sub: "Response within 2 hours guaranteed",
      color: "emerald"
    },
    {
      icon: MapPinIcon,
      label: "Regional Office",
      value: "Nairobi, Kenya",
      sub: "Heart of the Safari Capital",
      color: "amber"
    }
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-slate-950 text-white' : 'bg-neutral-50 text-slate-900'} font-['Outfit'] selection:bg-blue-500/30`}>
      <SEO
        title="Contact Us | SightTrails Safaris"
        description="Experience the soul of Africa. Connect with our curators to begin your bespoke safari expedition."
      />

      {/* Hero Section */}
      <section className="relative h-[45vh] sm:h-[55vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[10s] hover:scale-110"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80)` }} // Kenyan Grasslands
        />
        <div className={`absolute inset-0 ${isDark ? 'bg-slate-950/70' : 'bg-blue-900/30'} backdrop-blur-[2px]`} />
        
        <div className="relative z-10 text-center px-6 max-w-4xl">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-[10px] sm:text-xs font-black uppercase tracking-[0.4em] mb-4 ${isDark ? 'text-blue-400' : 'text-white'}`}
          >
            SightTrails Concierge
          </motion.p>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-7xl lg:text-8xl font-bold leading-tight tracking-tighter text-white"
          >
            Your African <br/>
            <span className="font-['Playfair_Display'] italic font-medium">Odyssey Begins.</span>
          </motion.h1>
        </div>
      </section>

      {/* Content Section */}
      <section className="relative z-20 -mt-20 px-6 pb-24 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          
          {/* Contact Methods (Left) */}
          <div className="lg:col-span-5 space-y-6">
            <motion.div 
               initial={{ opacity: 0, x: -30 }}
               whileInView={{ opacity: 1, x: 0 }}
               viewport={{ once: true }}
               className={`p-8 sm:p-10 rounded-[2.5rem] border ${isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-slate-200'} backdrop-blur-xl shadow-2xl`}
            >
              <h2 className="text-2xl font-bold mb-8 tracking-tight">Connect with us</h2>
              <div className="space-y-10">
                {contactMethods.map((method, idx) => (
                  <div key={idx} className="flex gap-6 group">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110 ${isDark ? 'bg-slate-800 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                      <method.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {method.label}
                      </p>
                      <p className="text-xl font-medium tracking-tight mb-1">{method.value}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{method.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className={`mt-12 pt-10 border-t ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
                <div className="flex items-center gap-4">
                  <ClockIcon className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <p className="text-sm font-medium">Standard Response Time: &lt; 2 Hours</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Form Section (Right) */}
          <div className="lg:col-span-7">
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className={`p-8 sm:p-12 rounded-[2.5rem] border ${isDark ? 'bg-slate-900/80 border-slate-800' : 'bg-white border-slate-200'} backdrop-blur-xl shadow-2xl`}
            >
              <AnimatePresence mode="wait">
                {isSubmitted ? (
                  <motion.div 
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="py-12 text-center"
                  >
                    <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-8 text-emerald-500">
                      <CheckCircleIcon className="w-12 h-12" />
                    </div>
                    <h3 className="text-3xl font-bold mb-4 tracking-tight">Expedition Logged</h3>
                    <p className={`max-w-md mx-auto mb-10 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      Our curatorial team has received your vision. We will orchestrate a response within the next 120 minutes.
                    </p>
                    <button 
                      onClick={() => setIsSubmitted(false)}
                      className="text-sm font-bold uppercase tracking-widest text-blue-600 border-b-2 border-blue-600/30 pb-1 hover:border-blue-600 transition-all"
                    >
                      Log Another Request
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="grid sm:grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <label className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Guest Name</label>
                        <input 
                          type="text" 
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full bg-transparent border-b-2 ${isDark ? 'border-slate-800 focus:border-blue-500' : 'border-slate-200 focus:border-blue-600'} py-3 outline-none transition-all text-lg font-medium`}
                          placeholder="Your Full Name"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Digital Address</label>
                        <input 
                          type="email" 
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full bg-transparent border-b-2 ${isDark ? 'border-slate-800 focus:border-blue-500' : 'border-slate-200 focus:border-blue-600'} py-3 outline-none transition-all text-lg font-medium`}
                          placeholder="email@example.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Expedition Tier</label>
                      <select 
                        name="tourInterest"
                        required
                        value={formData.tourInterest}
                        onChange={handleInputChange}
                        className={`w-full bg-transparent border-b-2 ${isDark ? 'border-slate-800 focus:border-blue-500 text-white' : 'border-slate-200 focus:border-blue-600 text-slate-900'} py-3 outline-none transition-all text-lg font-medium appearance-none cursor-pointer`}
                      >
                        <option value="" disabled className={isDark ? "bg-slate-900 text-slate-400" : "bg-white text-slate-400"}>Select Interest...</option>
                        <option value="luxury" className={isDark ? "bg-slate-900" : "bg-white"}>Pinnacle Luxury Safari</option>
                        <option value="cultural" className={isDark ? "bg-slate-900" : "bg-white"}>Authentic Cultural Immersion</option>
                        <option value="bespoke" className={isDark ? "bg-slate-900" : "bg-white"}>Custom Private Expedition</option>
                        <option value="photography" className={isDark ? "bg-slate-900" : "bg-white"}>Professional Wildlife Photography</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>The Vision</label>
                      <textarea 
                        name="message"
                        required
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        className={`w-full bg-transparent border-b-2 ${isDark ? 'border-slate-800 focus:border-blue-500' : 'border-slate-200 focus:border-blue-600'} py-3 outline-none transition-all text-lg font-light resize-none`}
                        placeholder="Detail the experience you wish to create..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full group relative overflow-hidden py-5 rounded-2xl flex items-center justify-center transition-all duration-300 font-bold tracking-widest uppercase text-xs sm:text-sm ${isDark ? 'bg-white text-slate-950 shadow-xl shadow-white/5' : 'bg-slate-950 text-white shadow-xl shadow-slate-950/20'} ${isSubmitting ? 'opacity-50' : 'hover:scale-[1.02] active:scale-98'}`}
                    >
                      <span className="relative z-10 flex items-center gap-3">
                        {isSubmitting ? (
                          <>
                            <ArrowPathIcon className="w-5 h-5 animate-spin" />
                            Transmitting...
                          </>
                        ) : (
                          <>
                            Commence Odyssey
                            <PaperAirplaneIcon className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                          </>
                        )}
                      </span>
                    </button>
                  </form>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Quote */}
      <section className={`py-24 ${isDark ? 'bg-slate-900/40' : 'bg-white'}`}>
        <div className="px-6 max-w-4xl mx-auto text-center">
          <ChatBubbleBottomCenterTextIcon className={`w-12 h-12 mb-8 mx-auto opacity-20 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <h3 className="text-3xl sm:text-5xl font-['Playfair_Display'] italic font-medium leading-[1.2] mb-8">
            "We don't just guide tours; we curate the moments that define your connection to the wild."
          </h3>
          <p className={`text-[10px] font-black uppercase tracking-[0.4em] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            — SightTrails Curatorial Philosophy
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;